import { useState, useMemo, useEffect, useCallback } from 'react';
import { DataGrid, KeenIcon } from '@/components';
import { ConfirmationDialog } from '@/components/dialogBox';
import usePagination from '@/hooks/usePagination';
import {
  useGetAllMembershipRequestsQuery,
  useReviewMembershipRequestMutation,
} from '@/services/ClubService';
import { MembershipRequestStatus } from '@/constant';
import { useParams } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDisplayDate } from '@/utils/Date';

interface User {
  id: string;
  name: string | null;
  email: string | null;
  mobile: string | null;
  profile_picture: string | null;
}

interface MembershipRequest {
  id: string;
  club_id: string;
  club_name: string;
  message: string;
  status: number;
  user: User;
  reviewer: User | null;
  previous_request: string | null;
  reason: string | null;
  created_at: string;
  updated_at: string;
}

interface IClubRequestData {
  id: string;
  name: string;
  email: string;
  phone: string;
  message: string;
  status: 'Pending' | 'Accepted' | 'Rejected';
  submitted_at: string;
}

const ClubRequestsListing = () => {
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();
  const [confirmActionState, setConfirmActionState] = useState<{
    isOpen: boolean;
    requestId: string | null;
    action: 'accept' | 'reject' | null;
    reason?: string;
  }>({ isOpen: false, requestId: null, action: null });
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = usePermissions();

  // Map status filter to MembershipRequestStatus enum value
  const getStatusEnum = (filter: string): number | undefined => {
    const statusMap: { [key: string]: number } = {
      pending: MembershipRequestStatus.PENDING, // 0
      accepted: MembershipRequestStatus.APPROVED, // 1
      rejected: MembershipRequestStatus.REJECTED, // 2
    };
    return filter !== 'all' ? statusMap[filter] : undefined;
  };

  // Fetch data from API with status and search parameters
  const queryParams = {
    page: pagination.pageIndex + 1,
    pageLimit: pagination.pageSize,
    search: debouncedValue,
    status: getStatusEnum(statusFilter),
    club_id: id || undefined,
  };

  const { data, isLoading, isFetching, isError, refetch } =
    useGetAllMembershipRequestsQuery(queryParams);

  // Mutation for reviewing membership request
  const [reviewMembershipRequest, { isLoading: isReviewing }] =
    useReviewMembershipRequestMutation();

  // Update pagination totals
  useEffect(() => {
    if (data) {
      handleTotalPages(data.totalPages || 1);
      handleTotalItems(data.totalItems || 0);
    }
  }, [data, handleTotalPages, handleTotalItems]);

  // Refetch on status filter or search term change
  useEffect(() => {
    refetch();
  }, [statusFilter, debouncedValue, refetch]);

  const transformData = (requests: MembershipRequest[]): IClubRequestData[] => {
    return requests.map((request) => ({
      id: request.id,
      name: request.user.name || 'Unknown',
      email: request.user.email || 'No email',
      phone: request.user.mobile || 'No phone',
      message: request.message,
      status:
        request.status === MembershipRequestStatus.PENDING
          ? 'Pending'
          : request.status === MembershipRequestStatus.APPROVED
            ? 'Accepted'
            : request.status === MembershipRequestStatus.REJECTED
              ? 'Rejected'
              : 'Pending',
      submitted_at: request.created_at,
    }));
  };

  const filteredData = useMemo(() => {
    if (!data?.data) return [];
    let result = transformData(data.data);
    // Apply client-side search filtering as a fallback
    if (debouncedValue) {
      result = result.filter(
        (item) =>
          item.name.toLowerCase().includes(debouncedValue.toLowerCase()) ||
          item.email.toLowerCase().includes(debouncedValue.toLowerCase())
      );
    }
    return result;
  }, [data, debouncedValue]);

  const resetFilters = () => {
    if (searchTerm || statusFilter !== 'all') {
      resetSearch();
      setStatusFilter('all');
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  };

  const handleActionClick = useCallback(
    (requestId: string, action: 'accept' | 'reject') => {
      setConfirmActionState({ isOpen: true, requestId, action });
    },
    []
  );

  const handleConfirmAction = async () => {
    if (confirmActionState.requestId && confirmActionState.action) {
      const reviewData = {
        status:
          confirmActionState.action === 'accept'
            ? MembershipRequestStatus.APPROVED.toString()
            : MembershipRequestStatus.REJECTED.toString(),
        reason:
          confirmActionState.action === 'reject'
            ? confirmActionState.reason
            : undefined,
      };

      try {
        await reviewMembershipRequest({
          id: confirmActionState.requestId,
          review: reviewData,
        }).unwrap();
        refetch();
        setConfirmActionState({
          isOpen: false,
          requestId: null,
          action: null,
          reason: undefined,
        });
      } catch (error) {
        console.error('Failed to review membership request:', error);
      }
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmActionState((prev) => ({ ...prev, reason: e.target.value }));
  };

  const columns = useMemo(
    () => [
      {
        accessor: (row: IClubRequestData) => row.email,
        id: 'email',
        header: 'Email',
        enableSorting: false,
        cell: ({ row }: { row: { original: IClubRequestData } }) => (
          <span className="text-gray-800 font-normal break-all md:break-normal">
            {row.original.email}
          </span>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessor: (row: IClubRequestData) => row.phone,
        id: 'phone',
        header: 'Phone',
        enableSorting: false,
        cell: ({ row }: { row: { original: IClubRequestData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.phone}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: IClubRequestData) => row.message,
        id: 'message',
        header: 'Message',
        enableSorting: false,
        cell: ({ row }: { row: { original: IClubRequestData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.message || '-'}
          </span>
        ),
        meta: { className: 'min-w-[250px]' },
      },
      {
        accessor: (row: IClubRequestData) => row.status,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: IClubRequestData } }) => (
          <span
            className={`text-sm font-medium ${
              row.original.status === 'Accepted'
                ? 'text-green-700 dark:text-green-600 contrast:text-green-600'
                : row.original.status === 'Rejected'
                  ? 'text-red-600'
                  : 'text-yellow-700 dark:text-yellow-600 contrast:text-yellow-600'
            }`}
          >
            {row.original.status}
          </span>
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        accessor: (row: IClubRequestData) => row.submitted_at,
        id: 'submitted_at',
        header: 'Submitted At',
        enableSorting: false,
        cell: ({ row }: { row: { original: IClubRequestData } }) => (
          <span className="text-gray-800 font-normal">
            {formatDisplayDate(new Date(row.original.submitted_at))}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: IClubRequestData } }) => (
          <div className="flex items-center gap-2">
            {row.original.status === 'Pending' &&
              hasPermission('review_membership_request') && (
                <>
                  <button
                    type="button"
                    onClick={() => handleActionClick(row.original.id, 'accept')}
                    className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-green-500"
                    title="Accept"
                    aria-label={`Accept ${row.original.name}'s application`}
                    disabled={isReviewing}
                  >
                    <KeenIcon
                      type="filled"
                      icon="check"
                      className="text-green-500"
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleActionClick(row.original.id, 'reject')}
                    className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-red-500"
                    title="Reject"
                    aria-label={`Reject ${row.original.name}'s application`}
                    disabled={isReviewing}
                  >
                    <KeenIcon
                      type="filled"
                      icon="cross"
                      className="text-red-500"
                    />
                  </button>
                </>
              )}
          </div>
        ),
        meta: { className: 'w-[120px]' },
      },
    ],
    [isReviewing, handleActionClick, hasPermission]
  );

  return (
    <section
      className="card card-grid min-w-full"
      role="region"
      aria-label="Club membership requests"
    >
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <div className="flex-shrink-0">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-requests"
              aria-label="Search club requests"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
              />
              <input
                id="search-requests"
                placeholder="Search requests"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search club requests by name or email"
                disabled={isLoading || isFetching || isReviewing}
              />
            </label>
          </div>
          <div className="flex-shrink-0">
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              className="select select-sm h-8 sm:h-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-[30px]"
              aria-label="Filter requests by status"
              disabled={isLoading || isFetching || isReviewing}
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(searchTerm || statusFilter !== 'all') && (
            <button
              className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={resetFilters}
              aria-label="Clear all filters"
              title="Clear all filters"
              disabled={isLoading || isFetching || isReviewing}
            >
              <KeenIcon
                type="solid"
                icon="arrows-circle"
                className="text-gray-600"
              />
              Clear Filters
            </button>
          )}
        </div>
      </div>
      <div className="card-body">
        {isLoading || isFetching || isReviewing ? (
          <div className="text-center py-4" role="status" aria-live="polite">
            <KeenIcon
              type="filled"
              icon="spinner"
              className="text-gray-500 text-2xl animate-spin"
            />
            <span className="ml-2 text-gray-700">Loading...</span>
          </div>
        ) : isError ? (
          <div className="text-center py-4 text-red-600" role="alert">
            Error loading data. Please try again.
          </div>
        ) : (
          <DataGrid
            columns={columns}
            data={filteredData}
            rowSelect={false}
            loading={isLoading || isFetching || isReviewing}
            pagination={{
              page: pagination.pageIndex,
              size: pagination.pageSize,
              totalItems: data?.totalItems || totalItems,
              totalPages: data?.totalPages || totalPages,
              setPagination,
              info: `{from} - {to} of {count}`,
              sizes: [5, 10, 25, 50, 100],
              sizesLabel: 'Show',
              sizesDescription: 'per page',
            }}
            sorting={[{ id: 'name', desc: false }]}
            serverSide={true}
            onFilterReset={resetFilters}
            hasActiveFilters={!!searchTerm || statusFilter !== 'all'}
            aria-label="Club membership requests table"
          />
        )}
        <ConfirmationDialog
          open={confirmActionState.isOpen}
          onClose={() =>
            setConfirmActionState({
              isOpen: false,
              requestId: null,
              action: null,
              reason: undefined,
            })
          }
          onConfirm={handleConfirmAction}
          message={
            confirmActionState.action === 'reject' ? (
              <>
                Are you sure you want to reject this membership request?
                <input
                  id="membership-reject-reason"
                  type="text"
                  name="membership_reject_reason"
                  aria-label="Rejection reason"
                  placeholder="Reason (optional)"
                  value={confirmActionState.reason || ''}
                  onChange={handleReasonChange}
                  className="mt-2 w-full p-2 border rounded bg-gray-200 placeholder:text-gray-400"
                  disabled={isReviewing}
                  autoComplete="off"
                />
              </>
            ) : (
              `Are you sure you want to ${confirmActionState.action} this membership request?`
            )
          }
          cancelButtonText="No"
          confirmButtonText="Yes"
          aria-labelledby="action-confirmation-title"
          aria-describedby="action-confirmation-description"
        />
      </div>
    </section>
  );
};

export { ClubRequestsListing };
